import React, { useState } from "react";
import { Loader2, Compass } from "lucide-react";
import { toast } from "sonner";
import { CompassLogo } from "@/components/CompassLogo";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { UI, t } from "@/i18n/translations";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TagInput } from "@/components/TagInput";
import { LanguageSelect } from "@/components/LanguageSelect";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

const TODAY = new Date().toISOString().split("T")[0];

export default function ProfileCompletion() {
  const { lang } = useLang();
  const { user, setUser } = useAuth();
  const P = UI.profileSetup;
  const A = UI.auth;
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    nom: user?.nom || "",
    residence: { address: user?.adresse_residence || "", lat: null, lon: null, verified: false },
    ecole_addr: { address: user?.adresse_ecole || "", lat: null, lon: null, verified: false },
    date_arrivee_en_france: user?.date_arrivee_en_france || "",
    ville: user?.ville || "",
    quartier: user?.quartier || "",
    langues_parlees: user?.langues_parlees || [],
    centres_interet: user?.centres_interet || [],
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Only ask what's missing (addresses always shown — they feed "My Landmarks")
  const showNom = !user?.nom?.trim();
  const showDate = !user?.date_arrivee_en_france;
  const showVille = !user?.ville;
  const showQuartier = !user?.quartier;
  const showLangues = !(user?.langues_parlees?.length);
  const showInterets = !(user?.centres_interet?.length);

  const finish = async () => {
    setSaving(true);
    try {
      const profilePayload = {};
      if (showNom && form.nom) profilePayload.nom = form.nom;
      if (showDate && form.date_arrivee_en_france) profilePayload.date_arrivee_en_france = form.date_arrivee_en_france;
      if (showVille && form.ville) profilePayload.ville = form.ville;
      if (showQuartier && form.quartier) profilePayload.quartier = form.quartier;
      if (showLangues && form.langues_parlees.length) profilePayload.langues_parlees = form.langues_parlees;
      if (showInterets && form.centres_interet.length) profilePayload.centres_interet = form.centres_interet;
      if (Object.keys(profilePayload).length) await api.put("/profile", profilePayload);

      // Prefill "My Landmarks" from the residence (and school) address
      if (form.residence.address.trim()) {
        try {
          await api.post("/reperes/search", {
            adresse_residence: form.residence.address,
            adresse_ecole: form.ecole_addr.address || null,
            residence_lat: form.residence.lat,
            residence_lon: form.residence.lon,
            ecole_lat: form.ecole_addr.lat,
            ecole_lon: form.ecole_addr.lon,
          });
        } catch (err) { console.warn("Landmarks prefill skipped (geocode/overpass best-effort):", err); }
      }
      const { data } = await api.post("/user/state", { profile_completed: true });
      setUser(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || UI.common.error[lang]);
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full rounded-2xl border border-line bg-white px-3.5 py-3 text-sm outline-none focus:border-amber transition-colors";

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="profile-completion">
      <div className="max-w-md mx-auto px-5 py-8">
        <div className="flex items-center gap-2 mb-6"><CompassLogo size={30} /><span className="font-display text-lg font-extrabold">Boussole</span></div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">{t(P.title, lang)}</h1>
        <p className="text-sm text-ink/60 mt-1 mb-6">{t(P.intro, lang)}</p>

        <div className="space-y-3">
          {showNom && (
            <Field label={t(P.nom, lang)}><input data-testid="setup-nom" maxLength={60} className={inputCls} value={form.nom} onChange={(e) => set("nom", e.target.value)} /></Field>
          )}

          <Field label={t(P.residence, lang)}>
            <AddressAutocomplete
              testId="setup-residence"
              value={form.residence}
              onChange={(v) => set("residence", v)}
              placeholder="12 rue de la Paix, Paris"
              iconColor="#E8A33D"
            />
          </Field>
          <Field label={t(P.ecoleAddress, lang)}>
            <AddressAutocomplete
              testId="setup-ecole-address"
              value={form.ecole_addr}
              onChange={(v) => set("ecole_addr", v)}
              placeholder={t(P.ecoleAddress, lang)}
              iconColor="#3E8E72"
            />
          </Field>
          <p className="flex items-start gap-2 text-xs text-sage leading-relaxed -mt-1">
            <Compass className="w-4 h-4 shrink-0 mt-0.5" /> {t(P.reperesNote, lang)}
          </p>

          {(showVille || showQuartier) && (
            <div className="grid grid-cols-2 gap-3">
              {showVille && <Field label={t(A.ville, lang)}><input data-testid="setup-ville" maxLength={60} className={inputCls} value={form.ville} onChange={(e) => set("ville", e.target.value)} /></Field>}
              {showQuartier && <Field label={t(A.quartier, lang)}><input data-testid="setup-quartier" maxLength={60} className={inputCls} value={form.quartier} onChange={(e) => set("quartier", e.target.value)} /></Field>}
            </div>
          )}
          {showDate && (
            <Field label={t(A.dateArrivee, lang)}><input type="date" max={TODAY} data-testid="setup-date" className={inputCls} value={form.date_arrivee_en_france} onChange={(e) => set("date_arrivee_en_france", e.target.value)} /></Field>
          )}
          {showLangues && (
            <Field label={t(A.langues, lang)}><LanguageSelect testId="setup-langues" value={form.langues_parlees} onChange={(v) => set("langues_parlees", v)} /></Field>
          )}
          {showInterets && (
            <Field label={t(A.interets, lang)}><TagInput testId="setup-interets" value={form.centres_interet} onChange={(v) => set("centres_interet", v)} placeholder={t(A.addTag, lang)} /></Field>
          )}
        </div>

        <p className="text-xs text-ink/45 mt-4">{t(P.skipField, lang)}</p>

        <button
          data-testid="finish-setup-btn"
          onClick={finish}
          disabled={saving}
          className="w-full mt-4 rounded-2xl bg-amber text-ink font-bold py-4 flex items-center justify-center gap-2 hover:brightness-105 active:scale-[0.99] transition disabled:opacity-60"
        >
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> {t(P.saving, lang)}</> : t(P.finish, lang)}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold mb-1.5 text-ink/80">{label}</span>
      {children}
    </label>
  );
}
